import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import clsx from 'clsx';
import produce from 'immer';
import { ModalHeader, Spinner, Modal, ModalBody, Media } from 'reactstrap';

import { fetcher } from 'api/client';
import useFileUpload from 'api/hooks/file/useFileUpload';
import { detectText, extractINEData } from 'api/actions/file';
import { clientContractAction } from 'api/actions/contracts';
import { convertToBase64 } from 'utils';
import { Camera } from 'components/Shared/Camera'
import { useHover } from 'hooks/useHover';
import useLocalStorage from 'hooks/useLocalStorage';
import { CONTROL_MODAL, INE_MODAL, useSign } from 'context/signview';

const initialState = {
    front: {},
    back: {}
}

export default function Ine() {

    const { t } = useTranslation();
    const [{ modal, contractId, contract, jwt }, dispatch] = useSign();
    const participant = contract?.signViewOptions?.currentParticipant || {};
    const [ineState, setIneState] = useLocalStorage(`INE-contract-${contractId}-${participant._id}`, initialState)
    const [showCamera, setShowCamera] = useState(false)
    const [selectedIne, setSelectedIne] = useState(null)

    const { mutate: mutateExtractINEData, isLoading: isLoadingExtract } = useMutation((payload) => fetcher(extractINEData(payload), jwt), {
        onSuccess: (data, variables) => {
            setIneState(produce(draft => {
                draft.front.extractedInfo = data.ineData;
            }))
        }
    });
    const { mutate: mutateDetectText, isLoading: isLoadingDetect } = useMutation((payload) => fetcher(detectText(payload), jwt), {
        onSuccess: (data, variables) => {
            console.log('extract info')
            setIneState(produce(draft => {
                draft.front.isTextDetected = true;
            }))
            mutateExtractINEData({ fileId: variables.fileId })
        }
    });
    const { mutate: mutateSendInformation, isLoading: isLoadingSend } = useMutation(({ contractId, action, participantId, ...payload }) => fetcher(clientContractAction(contractId, action, payload), jwt), {
        onSuccess: (data, variables) => {
            console.log('info sent')
            handleClose()
        }
    });
    const { uploadFile: uploadFrontIne, uploadFileState: uploadFrontIneState } = useFileUpload({
        jwt,
        onSuccess: (data) => {
            setIneState(produce(draft => {
                draft.front.isUploaded = true;
                draft.front.fileId = data.fileId;
            }))
            startTextExtraction(data.fileId)
        }
    })
    const { uploadFile: uploadBackIne, uploadFileState: uploadBackIneState } = useFileUpload({
        jwt,
        onSuccess: (data) => {
            setIneState(produce(draft => {
                draft.back.isUploaded = true;
            }))
        }
    })

    const startTextExtraction = (fileId) => {
        mutateDetectText({ fileId })
        // mutateExtractINEData({ fileId: ineState.front.fileId })
        // mutateExtractINEData({ fileId: '5ffdd99f8ec8787de86a4441'})
    }

    const handleClose = () => {
        dispatch({ type: CONTROL_MODAL, close: INE_MODAL })
    }

    const toggleCamera = () => setShowCamera(!showCamera);
    const onIneItemClick = (id) => {
        setSelectedIne(id)
        toggleCamera()
    }
    const handleCapture = (image) => {
        if (!selectedIne) return;

        setIneState(produce(draft => {
            draft[selectedIne].isLoaded = true;
            draft[selectedIne].image = new File([image], `ine-${selectedIne}.jpg`, {
                type: image.type
            });
        }))
    }
    const handleClear = () => {
        if (!selectedIne) return;

        setIneState(produce(draft => {
            draft[selectedIne].isLoaded = false;
            draft[selectedIne].image = undefined;
        }))
    }

    const handleGoBack = () => {
        setSelectedIne(null);
        setShowCamera(false);
    }

    const handleUpload = () => {
        if (ineState.front.fileId) {
            return startTextExtraction(ineState.front.fileId)
        }
        uploadFrontIne({
            file: ineState.front.image,
            name: ineState.front.image.name
        })
        uploadBackIne({
            file: ineState.back.image,
            name: ineState.back.image.name
        })
    }
    const handleSendInformation = () => {
        mutateSendInformation({
            contractId,
            action: 'sendineinformation',
            participantId: participant._id,
            fileId: ineState.front.fileId
        })
    }

    useEffect(() => {
        setIneState(produce(draft => {
            draft.front.isLoaded = draft.front.isLoaded && !!draft.front.fileId;
            draft.back.isLoaded = draft.front.isLoaded;

        }))
    }, [])

    return (
        <Modal isOpen={modal.includes(INE_MODAL)} centered>
            <ModalHeader toggle={handleClose}>
                <div className="text-capitalize font-weight-bold">Datos de tu INE</div>
                <div className="">Toma foto de tu INE y llena tus datos automáticamente.</div>
            </ModalHeader>
            <ModalBody>
                <Media>
                    <Media body className=" container position-relative">
                        {!ineState.front.extractedInfo ?
                            <div className="text-center">
                                {showCamera ?
                                    <Camera onCapture={handleCapture} onClear={handleClear} onGoBack={handleGoBack} />
                                    : (
                                        <div className="d-flex flex-column align-items-center">
                                            <IneItem
                                                id="front"
                                                className="mb-3"
                                                active={ineState.front.isLoaded}
                                                image={'/images/ine1.jpg'}
                                                text="Subir foto (frente)"
                                                onClick={onIneItemClick}
                                                isDisabled={uploadFrontIneState.isLoading || uploadBackIneState.isLoading}
                                                isUploading={uploadFrontIneState.isLoading}
                                                isExtracting={isLoadingExtract || isLoadingDetect}
                                            />
                                            <IneItem
                                                id="back"
                                                active={ineState.back.isLoaded}
                                                image={'/images/ine2.jpg'}
                                                text="Subir foto (reverso)"
                                                onClick={onIneItemClick}
                                                isDisabled={uploadFrontIneState.isLoading || uploadBackIneState.isLoading}
                                                isUploading={uploadBackIneState.isLoading}
                                                isExtracting={isLoadingExtract || isLoadingDetect}
                                            />
                                        </div>
                                    )
                                }
                                {!showCamera &&

                                    <button
                                        className="btn btn-primary mt-2 mx-2"
                                        onClick={handleUpload}
                                        disabled={!ineState.front.isLoaded || !ineState.back.isLoaded || uploadFrontIneState.isLoading || uploadBackIneState.isLoading}
                                    >
                                        {uploadFrontIneState.isLoading || uploadBackIneState.isLoading ?
                                            'Subiendo...'
                                            : isLoadingExtract || isLoadingDetect ?
                                                'Extrayendo...'
                                                : 'Subir'
                                        }
                                    </button>
                                }
                            </div>
                            :
                            <div>
                                <div className="p-2">
                                    <IneProperty label="Apellido(s)" value={`${ineState.front.extractedInfo.lastName ?? ''} ${ineState.front.extractedInfo.lastName2 ?? ''}`} />
                                    <IneProperty label="Nombre(s)" value={ineState.front.extractedInfo.name} />
                                    <IneProperty label="Sexo" value={ineState.front.extractedInfo.gender} />
                                    <IneProperty label="Fecha de nacimiento" value={ineState.front.extractedInfo.birthDate} />
                                    <IneProperty label="Domicilio" value={ineState.front.extractedInfo.address} />
                                    <IneProperty label="CURP" value={ineState.front.extractedInfo.curp} />
                                    <IneProperty label="Clave de elector" value={ineState.front.extractedInfo.claveElector} />
                                </div>
                                <div className="text-center">
                                    <button
                                        className="btn btn-primary mt-2 mx-2"
                                        onClick={handleSendInformation}
                                        disabled={isLoadingSend}
                                    >
                                        Enviar informacion
                                        </button>
                                </div>
                            </div>
                        }
                    </Media>
                </Media>
            </ModalBody>
        </Modal>
    )
}

function IneProperty({ label, value }) {
    return (
        <div className="d-flex flex-column p-1">
            <strong className="text-uppercase text-primary"><small>{label}</small></strong> {value}
        </div>
    )
}


function IneItem({ className, id, active, image, text, isUploading, isExtracting, isDisabled, onClick }) {
    const [hoverRef, isHovered] = useHover();

    const handleClick = () => {
        if (isDisabled) return;
        onClick(id)
    }

    return (
        <div ref={hoverRef} className={clsx('rounded border p-3 d-flex align-items-center', className, isHovered && !isDisabled && 'border-primary pointer')} style={{ width: 300 }} onClick={handleClick}>
            <div className="px-2">
                {active ? <i className="fas fa-check-circle text-success"></i> : <i className="fas fa-circle text-muted" ></i>}
            </div>
            <img src={image} style={{ width: 60, height: 40 }} />
            <div className="px-2 w-100">
                {text}
            </div>
            {(isUploading || isExtracting) &&
                <div>
                    <Spinner size="xs" />
                </div>
            }
        </div>
    )
}